import "./App.css";

import { Outlet, useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { initHotJar } from "./utils/HotJar";

import { useCookies } from "react-cookie";
import { cookiePreferences, getDefectMappings } from "@idwal/idwal-utilities";
import { COOKIE_CONSENT } from "@idwal/idwal-utilities/lib/constants/cookies";
import { initializeAnalytics, sendGAEvent } from "./services/AnalyticsService";
import { QueryClientProvider } from "react-query";
import { useSetupQueryClient } from "./config/queryClient";
import { useFlags } from "launchdarkly-react-client-sdk";
import AppLayout from "./components/AppLayout";
import { AppContextType } from "./types/AppContextType";
import { ApplicationWrapper, buildNavigationMenus } from "@idwal/idwal-react-components";
import {
    ANALYTICS_TAB_CLICK,
    DEFECTS_CLICK,
    GRADE_CLICK,
    HOME_CLICK, ORDERS_CLICK,
    QUOTATIONS_CLICK, REPORTS_CLICK,
    SUB_GRADE_CLICK,
    SCREENING_CLICK,
    DASHBOARD_CLICK
} from "./utils/analytics";
import { AUTH_TIMEOUT, AUTH_URL_PROTOCOL } from "./cognito/authConfig";
import { useAppDispatch, useAppSelector } from "./redux/hooks";
import { fetchUserToken, getUserCapabilitiesThunk, userSliceSelector } from "./features/userSlice";
import { useCapabilities } from "./hooks/user";
import { Capability } from "./types/inspections/UserCapabilities";
import { PageHeaderWrapper } from "./components/page-header/PageHeaderWrapper";
import { INSPECTION_ROUTE, INSPECTIONS_ROUTE, ORDERS_ROUTE, QUOTATIONS_ROUTE } from "./config/router";
import { InstantQuotation } from "./components/InstantQuotation/InstantQuotation";
import { getInspectionTypesThunk, getLocationsThunk } from "./features/instantQuotationSlice";
import { VesselFinder } from "./components/InstantQuotation/VesselFinder";
import { DeleteQuotation } from "./components/InstantQuotation/DeleteQuotation";
import OrderInspectionModal from "./components/order-inspection/OrderInspectionModal";
import { setAuthorityMappings } from "./features/inspectionsSlice";

export const AppContext = React.createContext<AppContextType | null>(null);

let hotjarInitialised = false;

function App() {
    const [restrictedView, setRestrictedView] = useState<boolean>(false);
    const [earlyRelease, setEarlyRelease] = useState<boolean>(false);
    const {
        showInspectionsMyReports,
        showInspectionsQuotes,
        showMonitoringDefects,
        showMonitoringGrades,
        showInspectionsOrders,
        showMonitoringMyVessels,
        showMonitoringSubGrades,
        showScreeningMyPortfolio,
        showScreeningVetting,
        showMonitoringPortStateControl,
        releaseAuthorityMappings,
        showMonitoringRectifications,
    } = useFlags();
    const [idwalSideBarOpen, setIdwalSideBarOpen] = useState(false);
    const {UserCapabilityStatus} = useAppSelector(userSliceSelector);
    const dispatch = useAppDispatch();
    const { authStatus } = useAuthenticator();

    const navigate = useNavigate();

    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);

    const [cookies, setCookie] = useCookies([COOKIE_CONSENT]);
    const cookieUtils = cookiePreferences(setCookie, cookies);
    const inspectionId = window.location.pathname.split("/")?.[2] ?? "TEST ID";
    const routePath = '/' + location.pathname.split('/')[1].toLowerCase();

    useEffect(() => {
        if (user && UserCapabilityStatus === "idle") {
            dispatch(getUserCapabilitiesThunk({userId: user.username!}));
            dispatch(fetchUserToken());
        }
    }, [user]);

    useEffect(() => {
        if (releaseAuthorityMappings) {
            // put back into above useEffect when flag no longer needed
            dispatch(setAuthorityMappings(getDefectMappings()));
        }
    }, [releaseAuthorityMappings])

    // If we don't get authenticated within a specific amount of time then send
    // the user out to the SSO login screen.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (authStatus !== "authenticated") {
                redirectToSSO();
            }
        }, AUTH_TIMEOUT);
        return () => clearTimeout(timer);
    }, [authStatus, user]);

    const redirectToSSO = () => {
        window.location.assign(
            `${AUTH_URL_PROTOCOL}://${process.env.REACT_APP_AUTH_URL}?redirect=${window.location.href}`
        );
    };

    const { capabilities } = useCapabilities([
        Capability.CAP_FLEETAPP_VIEW_DEFECTS,
        Capability.CAP_FLEETAPP_VIEW_FLEETS,
        Capability.CAP_FLEETAPP_VIEW_GRADES,
        Capability.CAP_FLEETAPP_VIEW_SUBGRADES,
        Capability.CAP_FLEETAPP_VIEW_RECTIFICATIONS,
        Capability.CAP_FLEETAPP_NOTIFICATIONS,
        Capability.CAP_FLEETAPP_VIEW_PSC,
        Capability.CAP_FLEETAPP_VIEW_SCREENING,
        Capability.CAP_FLEETAPP_DASHBOARD,
        Capability.CAP_FLEETAPP_ASK_IDWAL_AI
    ]);

    const { appSidebarItems, idwalSidebarIdItems, idwalSidebarOtherItems, idwalSidebarScreeningItems } = buildNavigationMenus(
        capabilities,
        {
            links: {
                dashboardLink: `${process.env.REACT_APP_FLEET_DOMAIN}.idwalmarine.com`, // dashboard is now the home/root route
                myVesselsLink: `${process.env.REACT_APP_FLEET_DOMAIN}.idwalmarine.com/myfleet`,
                defectsLink: `${process.env.REACT_APP_FLEET_DOMAIN}.idwalmarine.com/defects`,
                gradesLink: `${process.env.REACT_APP_FLEET_DOMAIN}.idwalmarine.com/grade`,
                subgradesLink: `${process.env.REACT_APP_FLEET_DOMAIN}.idwalmarine.com/subgrades`,
                rectificationsLink: `${process.env.REACT_APP_FLEET_DOMAIN}.idwalmarine.com/shareddefects`,
                pscLink: `${process.env.REACT_APP_FLEET_DOMAIN}.idwalmarine.com/psc`,
                screeningLink: `${process.env.REACT_APP_ID_PLATFORM_SCREENING_URL_ROOT}/screening`,
            },
            flags: {
                // Dashboard visibility is now gated on the capability, not a LaunchDarkly flag.
                showMonitoringDashboard: true,
                showMonitoringDefects,
                showMonitoringGrades,
                showMonitoringMyVessels,
                showMonitoringSubGrades,
                showMonitoringPortStateControl,
                showScreeningMyPortfolio,
                showScreeningVetting,
                showMonitoringRectifications
            },
            callbacks: {
                clickMonitoringDashboard: (to: string) => {
                    setIdwalSideBarOpen(false);
                    sendGAEvent(ANALYTICS_TAB_CLICK, DASHBOARD_CLICK);
                    window.location.replace(`${AUTH_URL_PROTOCOL}://${to}`);
                },
                clickMonitoringMyVessels: (to: string) => {
                    setIdwalSideBarOpen(false);
                    sendGAEvent(ANALYTICS_TAB_CLICK, HOME_CLICK);
                    window.location.replace(`${AUTH_URL_PROTOCOL}://${to}`);
                },
                clickMonitoringDefects: (to: string) => {
                    setIdwalSideBarOpen(false);
                    sendGAEvent(ANALYTICS_TAB_CLICK, DEFECTS_CLICK);
                    window.location.replace(`${AUTH_URL_PROTOCOL}://${to}`);
                },
                clickMonitoringGrades: (to: string) => {
                    setIdwalSideBarOpen(false);
                    sendGAEvent(ANALYTICS_TAB_CLICK, GRADE_CLICK);
                    window.location.replace(`${AUTH_URL_PROTOCOL}://${to}`);
                },
                clickMonitoringSubGrades: (to: string) => {
                    setIdwalSideBarOpen(false);
                    sendGAEvent(ANALYTICS_TAB_CLICK, SUB_GRADE_CLICK);
                    window.location.replace(`${AUTH_URL_PROTOCOL}://${to}`);
                },
                clickMonitoringRetifications: (to: string) => {
                    setIdwalSideBarOpen(false);
                    window.location.replace(`${AUTH_URL_PROTOCOL}://${to}`);
                },
                clickMonitoringPSC: (to: string) => {
                    setIdwalSideBarOpen(false);
                    window.location.replace(`${AUTH_URL_PROTOCOL}://${to}`);
                },
                clickMonitoringScreening: (to: string) => {
                    setIdwalSideBarOpen(false);
                    window.location.replace(to);
                },
            }
        },
        {
            links: {
                myReportsLink: `/${INSPECTIONS_ROUTE}`,
                inspectionOrdersLink: `/${ORDERS_ROUTE}`,
                inspectionQuotesLink: `/${QUOTATIONS_ROUTE}`
            },
            flags: {
                showInspectionsMyReports,
                showOrders: showInspectionsOrders,
                showInspectionsQuotes
            },
            callbacks: {
                clickInspectionsMyReports: (to: string) => {
                    sendGAEvent(ANALYTICS_TAB_CLICK, REPORTS_CLICK);
                    navigate(to);
                },
                clickInspectionsOrders: (to: string) => {
                    sendGAEvent(ANALYTICS_TAB_CLICK, ORDERS_CLICK);
                    navigate(to);
                },
                clickInspectionsQuotes: (to: string) => {
                    sendGAEvent(ANALYTICS_TAB_CLICK, QUOTATIONS_CLICK);
                    navigate(to);
                },
            }
        },
        {
            links: {
                myPortfolioLink: `${process.env.REACT_APP_ID_PLATFORM_SCREENING_URL_ROOT}/screening`,
                screeningLink: `${process.env.REACT_APP_FLEET_DOMAIN}.idwalmarine.com/screening-placeholder`
            },
            flags: {
                showScreeningMyPortfolio,
                showScreeningVetting
            },
            callbacks: {
                clickScreeningMyPortfolio: (to: string) => {
                    setIdwalSideBarOpen(false);
                    sendGAEvent(ANALYTICS_TAB_CLICK, SCREENING_CLICK);
                    window.location.replace(to);
                },
                clickScreeningVetting: () => {}
            }
        },
        routePath === '/inspection' ? '/inspections' : routePath
    );

    const queryClient = useSetupQueryClient();

    useEffect(() => {
        if (user) {
            initializeAnalytics({
                inspectionId: inspectionId,
                customerId: user.attributes?.email ?? "",
                cognitoId: user.attributes?.sub ?? ""
            });
        }
    }, [user]);

    useEffect(() => {
        dispatch(getInspectionTypesThunk());
        dispatch(getLocationsThunk());
    }, []);

    if (
        !hotjarInitialised &&
        user &&
        (cookies[COOKIE_CONSENT]?.all === true ||
            cookies[COOKIE_CONSENT]?.analytics === true)
    ) {
        hotjarInitialised = initHotJar(user);
    }

    const providerValues = useMemo(() => ({ restrictedView  , setRestrictedView , earlyRelease, setEarlyRelease }),
        [restrictedView,earlyRelease]); // value is cached by useMemo

    return (
        <AppContext.Provider value={providerValues}>
            <QueryClientProvider client={queryClient}>
                <ApplicationWrapper
                    showNotifications={false}
                    loadingNotifications={false}
                    mappedNotifications={[]}
                    burgerMenuLogo={"main"}
                    logoutLink={`${AUTH_URL_PROTOCOL}://${process.env.REACT_APP_AUTH_URL}/logout?redirect=${window.location.href}`}
                    idwalSidebarIdItems={idwalSidebarIdItems}
                    idwalSidebarOtherItems={idwalSidebarOtherItems}
                    idwalSidebarScreeningItems={idwalSidebarScreeningItems}
                    cookieUtils={cookieUtils}
                    appSidebarItems={appSidebarItems}
                    activeAppSidebarItem={routePath === `/${INSPECTION_ROUTE}` ? `/${INSPECTIONS_ROUTE}` : routePath}
                    markAllNotificationsAsRead={() => {}}
                    idwalSidebarOpen={idwalSideBarOpen}
                    setIdwalSideBarOpen={(open) => setIdwalSideBarOpen(open)}
                >
                    <AppLayout>
                        <PageHeaderWrapper enableAskIdwalAI={capabilities[Capability.CAP_FLEETAPP_ASK_IDWAL_AI]} />
                        <Outlet />
                    </AppLayout>
                </ApplicationWrapper>
                <InstantQuotation />
                <VesselFinder />
                <OrderInspectionModal />
                <DeleteQuotation />
            </QueryClientProvider>
        </AppContext.Provider>
    );
}
export default App;
